import { useState, FC } from 'react';

import { Box, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import { ServerDataGrid } from '@shared/components';
import { useServerDataGridRefresh } from '@shared/hooks';
import { tableStyles } from '@shared/styles';
import { ReferralRewardUserProgressModel } from '@shared/types';
import { getFilterOperators, getObjectIdConvertedTimestamp } from '@shared/utils';

import { useLazyGetUserProgressesQuery } from '@services';

import {
  getReferralUserProgressesListSelector,
  getReferralUserProgressesPageSelector,
  getReferralUserProgressesTotalCountSelector,
  refreshReferralTable,
  refreshUserProgressesTableSelector,
  setReferralUserProgress,
  setReferralUserProgressPage,
  setReferralUserProgressTotalCount,
} from '@store';

import { REFERRAL_USERS_PROGRESSES_PAGE_LIMIT } from './constants';
import { ReferralUsersListProps } from './types';
import { UserTableActionsMenu } from './UserTableActionsMenu';

const { tableContainer } = tableStyles;

export const ReferralUsersList: FC<ReferralUsersListProps> = ({
  serverDataGridRef,
  refreshTable,
  handleUserProgressEditModalOpen,
  handleAssignUserModalOpen,
  referralSystemStartDate = new Date(),
}) => {
  const [filter, setFilter] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<GridSortModel>([{ field: 'receivedRewardValue', sort: 'desc' }]);

  useServerDataGridRefresh(refreshUserProgressesTableSelector, refreshReferralTable, refreshTable);

  const userColumns: GridColDef[] = [
    {
      field: 'userNickname',
      headerName: 'User Nickname',
      minWidth: 200,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, ReferralRewardUserProgressModel>) => {
        const {
          row: { userNickname, userId },
        } = params;

        return (
          <Typography key={userId} overflow="hidden" textOverflow="ellipsis">
            {userNickname}
          </Typography>
        );
      },
    },
    {
      field: 'receivedRewardValue',
      headerName: 'Received Reward',
      maxWidth: 130,
      flex: 1,
      hideable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, ReferralRewardUserProgressModel>) => {
        const {
          row: { receivedRewardValue, userId },
        } = params;

        return (
          <Typography key={userId} overflow="hidden" textOverflow="ellipsis">
            {receivedRewardValue}
          </Typography>
        );
      },
    },
    {
      field: 'totalSpinsCount',
      headerName: 'Spins Count',
      maxWidth: 130,
      flex: 1,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, ReferralRewardUserProgressModel>) => {
        const {
          row: { totalSpinsCount, userId },
        } = params;

        return (
          <Typography key={userId} overflow="hidden" textOverflow="ellipsis">
            {totalSpinsCount}
          </Typography>
        );
      },
    },
    {
      field: 'referralsCount',
      headerName: 'Referrals Count',
      maxWidth: 130,
      flex: 1,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, ReferralRewardUserProgressModel>) => {
        const {
          row: { referralInviter, userId },
        } = params;

        const referralsCount = referralInviter
          ? referralInviter.referralIds.filter(
              (referralId: any) => getObjectIdConvertedTimestamp(referralId) >= new Date(referralSystemStartDate)
            ).length
          : 0;

        return (
          <Typography key={userId} overflow="hidden" textOverflow="ellipsis">
            {referralsCount}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      maxWidth: 100,
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, ReferralRewardUserProgressModel>) => {
        const { row } = params;

        return (
          <UserTableActionsMenu
            userProgress={row}
            handleUserProgressEditModalOpen={handleUserProgressEditModalOpen}
            handleAssignUserModalOpen={handleAssignUserModalOpen}
          />
        );
      },
    },
  ];

  return (
    <>
      <Box className={tableContainer}>
        <ServerDataGrid<ReferralRewardUserProgressModel>
          ref={serverDataGridRef}
          columns={userColumns}
          pageSelector={getReferralUserProgressesPageSelector}
          setPageAction={setReferralUserProgressPage}
          totalCountSelector={getReferralUserProgressesTotalCountSelector}
          setTotalCountAction={setReferralUserProgressTotalCount}
          listSelector={getReferralUserProgressesListSelector}
          setListAction={setReferralUserProgress}
          limit={REFERRAL_USERS_PROGRESSES_PAGE_LIMIT}
          filter={filter}
          setFilter={setFilter}
          sort={sort}
          setSort={setSort}
          useLazyListQuery={useLazyGetUserProgressesQuery}
        />
      </Box>
    </>
  );
};
